import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "cn";
import { createContext, useContext } from "react";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "#/components/Tooltip/Tooltip";

const statusIndicatorVariants = cva(
	"font-medium inline-flex items-center gap-2",
	{
		variants: {
			variant: {
				success: "text-content-success",
				failed: "text-content-destructive",
				inactive: "text-content-secondary",
				warning: "text-content-warning",
				pending: "text-highlight-sky",
			},
			size: {
				sm: "text-xs",
				md: "text-sm",
			},
		},
		defaultVariants: {
			variant: "success",
			size: "md",
		},
	},
);

type StatusIndicatorContextValue = VariantProps<typeof statusIndicatorVariants>;

const StatusIndicatorContext = createContext<StatusIndicatorContextValue>({});

export type StatusIndicatorProps = React.ComponentProps<"div"> &
	StatusIndicatorContextValue;

export const StatusIndicator: React.FC<StatusIndicatorProps> = ({
	size,
	variant,
	className,
	...props
}) => {
	return (
		<StatusIndicatorContext.Provider value={{ size, variant }}>
			<div
				className={cn(statusIndicatorVariants({ variant, size }), className)}
				{...props}
			/>
		</StatusIndicatorContext.Provider>
	);
};

const dotVariants = cva("rounded-full inline-block border-4 border-solid", {
	variants: {
		variant: {
			success: "bg-content-success border-surface-green",
			failed: "bg-content-destructive border-surface-destructive",
			inactive: "bg-content-secondary border-surface-grey",
			warning: "bg-content-warning border-surface-orange",
			pending: "bg-highlight-sky border-surface-sky",
		},
		size: {
			sm: "size-3 border-4",
			md: "size-4 border-4",
		},
	},
	defaultVariants: {
		variant: "success",
		size: "md",
	},
});

type StatusIndicatorDotProps = React.ComponentProps<"div"> &
	VariantProps<typeof dotVariants>;

export const StatusIndicatorDot: React.FC<StatusIndicatorDotProps> = ({
	className,
	// We allow the size and variant to be overridden directly by the component.
	// This allows StatusIndicatorDot to be used alone.
	size,
	variant,
	...props
}) => {
	const { size: ctxSize, variant: ctxVariant } = useContext(
		StatusIndicatorContext,
	);

	return (
		<div
			className={cn(
				dotVariants({ variant: variant ?? ctxVariant, size: size ?? ctxSize }),
				className,
			)}
			{...props}
		/>
	);
};

type StatusHealthyIndicatorProps = {
	derpOnly?: boolean;
};

export const StatusHealthyIndicator: React.FC<StatusHealthyIndicatorProps> = ({
	derpOnly,
}: StatusHealthyIndicatorProps) => {
	return (
		<StatusIndicator variant="success">
			<StatusIndicatorDot />
			{derpOnly ? "Healthy (DERP only)" : "Healthy"}
		</StatusIndicator>
	);
};

export const StatusNotHealthyIndicator: React.FC = () => {
	return (
		<StatusIndicator variant="failed">
			<StatusIndicatorDot />
			Unhealthy
		</StatusIndicator>
	);
};

export const StatusNotRegisteredIndicator: React.FC = () => {
	return (
		<Tooltip>
			<TooltipTrigger asChild>
				<StatusIndicator variant="warning">
					<StatusIndicatorDot />
					Never seen
				</StatusIndicator>
			</TooltipTrigger>
			<TooltipContent>
				Workspace Proxy has never come online and needs to be started.
			</TooltipContent>
		</Tooltip>
	);
};

export const StatusNotReachableIndicator: React.FC = () => {
	return (
		<Tooltip>
			<TooltipTrigger asChild>
				<StatusIndicator variant="warning">
					<StatusIndicatorDot />
					Not reachable
				</StatusIndicator>
			</TooltipTrigger>
			<TooltipContent>
				Workspace Proxy not responding to http(s) requests.
			</TooltipContent>
		</Tooltip>
	);
};
